import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { PageHeader, Panel } from "../../components/ui";
import AttachmentPicker from "components/SupportTickets/AttachmentPicker";
import { pasteTicketFiles, ticketFormData } from "components/SupportTickets/supportTicketStatus";
import { partnerQueryKeys } from "../../api/queryKeys";
import { openSupportTicket } from "../../api/endpoints/support";

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 8000;

/** A ticket is for one specific issue: a clear title, what happened, and any screenshots. */
const NewSupportTicketPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);

  const open = useMutation(
    () =>
      openSupportTicket(
        ticketFormData({ title: title.trim(), description: description.trim() }, files)
      ),
    {
      onSuccess: (ticket) => {
        toast.success("Support ticket opened");
        queryClient.invalidateQueries(partnerQueryKeys.support());
        navigate(`/partner/help/tickets/${ticket.id}`, { replace: true });
      },
      onError: (error) =>
        toast.error(error?.response?.data?.errorMessage || "The ticket could not be opened."),
    }
  );

  const ready = title.trim() && description.trim() && !open.isLoading;

  return (
    <div>
      <Link
        to="/partner/help"
        className="mb-4 inline-block text-xs font-semibold text-teal-700 no-underline hover:text-teal-800"
      >
        ← Back to Help &amp; Support
      </Link>
      <PageHeader title="New support ticket" />
      <Panel>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) open.mutate();
          }}
        >
          <TextField
            fullWidth
            required
            size="small"
            label="Title"
            placeholder="e.g. Commission for Sita Rai not showing"
            value={title}
            inputProps={{ maxLength: TITLE_MAX }}
            onChange={(event) => setTitle(event.target.value)}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={5}
            label="Describe the issue"
            helperText="What happened, what you expected, and any client or payment it concerns."
            value={description}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            onChange={(event) => setDescription(event.target.value)}
            onPaste={(event) => {
              if (open.isLoading) return;
              pasteTicketFiles(event, {
                files,
                onChange: setFiles,
                onProblem: (problem) => toast.error(problem),
              });
            }}
          />
          <div>
            <p className="m-0 mb-1 text-xs text-slate-500">
              Screenshots or PDFs, up to 5 files of 10 MB each. You can also paste them into the
              description.
            </p>
            <AttachmentPicker
              files={files}
              onChange={setFiles}
              onProblem={(problem) => toast.error(problem)}
              disabled={open.isLoading}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => navigate("/partner/help")} disabled={open.isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!ready}>
              {open.isLoading ? "Opening…" : "Open ticket"}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
};

export default NewSupportTicketPage;
